import * as React from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/design-system/lib/utils';

export interface BottomActionBarProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * Standardized sticky action bar pinned to the bottom of the viewport, offset
 * for the side nav on large screens. Used for bulk/selection actions across the
 * app (reward payouts, affiliate applications, …). Pass the bar's content as
 * `children`; this component only owns the chrome (position, background, border,
 * max-width container).
 *
 * Portals to <body> so its `position: fixed` resolves against the viewport
 * rather than a transformed ancestor: the view-enter animation wrapper applies
 * `transform: translateY(0)`, which creates a containing block that would
 * otherwise scope the fixed positioning to it instead of the viewport.
 */
const BottomActionBar = React.forwardRef<HTMLDivElement, BottomActionBarProps>(
  ({ children, className, ...props }, ref) => {
    const [mounted, setMounted] = React.useState(false);
    React.useEffect(() => setMounted(true), []);

    if (!mounted) return null;

    return createPortal(
      <div
        ref={ref}
        className={cn(
          'tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:min-[1200px]:pl-[280px] tw:py-2 tw:border-t tw:border-border tw:bg-white dark:tw:bg-neutral-900 tw:z-[1100]',
          className,
        )}
        {...props}
      >
        <div className="tw:w-full tw:max-w-[1440px] tw:mx-auto tw:px-4">{children}</div>
      </div>,
      document.body,
    );
  },
);

BottomActionBar.displayName = 'BottomActionBar';

export { BottomActionBar };
